import { cn } from '@/lib/cn';

/** Gold filigree corner. Rotate for each corner. */
function Corner({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 48 48" className={className} style={style} aria-hidden>
      <defs>
        <linearGradient id="hk-corner-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F3DC9C" />
          <stop offset=".55" stopColor="#C9A35A" />
          <stop offset="1" stopColor="#8E6F34" />
        </linearGradient>
      </defs>
      <g fill="none" stroke="url(#hk-corner-g)" strokeLinecap="round">
        <path d="M2 30V8a6 6 0 0 1 6-6h22" strokeWidth="1.6" />
        <path d="M7 24V12a5 5 0 0 1 5-5h12" strokeWidth=".9" opacity=".7" />
        <path d="M2 8c6 0 9 3 9 9M8 2c0 6 3 9 9 9" strokeWidth=".9" opacity=".8" />
        <circle cx="12.5" cy="12.5" r="2" fill="url(#hk-corner-g)" stroke="none" />
      </g>
    </svg>
  );
}

/** Four filigree corners positioned inside a relatively-positioned parent. */
export function CornerFlourishes({
  size = 34,
  inset = 6,
  className,
}: {
  size?: number;
  inset?: number;
  className?: string;
}) {
  const s = { width: size, height: size } as const;
  return (
    <div className={cn('pointer-events-none absolute inset-0', className)} aria-hidden>
      <Corner className="absolute" style={{ ...s, left: inset, top: inset }} />
      <Corner className="absolute" style={{ ...s, right: inset, top: inset, transform: 'scaleX(-1)' }} />
      <Corner className="absolute" style={{ ...s, left: inset, bottom: inset, transform: 'scaleY(-1)' }} />
      <Corner className="absolute" style={{ ...s, right: inset, bottom: inset, transform: 'scale(-1,-1)' }} />
    </div>
  );
}

/** Horizontal ornamental divider with a center diamond. */
export function OrnateDivider({ className, label }: { className?: string; label?: React.ReactNode }) {
  return (
    <div className={cn('flex items-center gap-3 text-gold/80', className)} aria-hidden={!label}>
      <span className="h-px flex-1 bg-[linear-gradient(90deg,transparent,rgba(201,163,90,.6))]" />
      <svg viewBox="0 0 40 12" className="h-3 w-10 shrink-0">
        <path d="M0 6h14M26 6h14" stroke="currentColor" strokeWidth=".8" opacity=".7" />
        <path d="M20 1l5 5-5 5-5-5z" fill="currentColor" />
        <circle cx="11" cy="6" r="1.2" fill="currentColor" />
        <circle cx="29" cy="6" r="1.2" fill="currentColor" />
      </svg>
      {label && <span className="hk-eyebrow !text-gold/80">{label}</span>}
      {label && (
        <svg viewBox="0 0 40 12" className="h-3 w-10 shrink-0">
          <path d="M0 6h14M26 6h14" stroke="currentColor" strokeWidth=".8" opacity=".7" />
          <path d="M20 1l5 5-5 5-5-5z" fill="currentColor" />
          <circle cx="11" cy="6" r="1.2" fill="currentColor" />
          <circle cx="29" cy="6" r="1.2" fill="currentColor" />
        </svg>
      )}
      <span className="h-px flex-1 bg-[linear-gradient(270deg,transparent,rgba(201,163,90,.6))]" />
    </div>
  );
}

/** A gilded hex with an inner keep — an ornament (the brand mark is FablekeepMark). */
export function HexSigil({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id="hk-sigil-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F6E2A8" />
          <stop offset=".5" stopColor="#C9A35A" />
          <stop offset="1" stopColor="#7C5E2A" />
        </linearGradient>
        <radialGradient id="hk-sigil-bg" cx=".5" cy=".35" r=".7">
          <stop offset="0" stopColor="#2A231C" />
          <stop offset="1" stopColor="#0E0C0A" />
        </radialGradient>
      </defs>
      <path
        d="M32 3 57 17.5v29L32 61 7 46.5v-29z"
        fill="url(#hk-sigil-bg)"
        stroke="url(#hk-sigil-g)"
        strokeWidth="2.4"
      />
      <path
        d="M32 10.5 50.5 21.2v21.6L32 53.5 13.5 42.8V21.2z"
        fill="none"
        stroke="url(#hk-sigil-g)"
        strokeWidth=".8"
        opacity=".55"
      />
      <g fill="url(#hk-sigil-g)">
        <path d="M22 44V28l3-2v-4h3v3h2v-4h4v4h2v-3h3v4l3 2v16h-7v-7a3 3 0 0 0-6 0v7z" />
      </g>
      <circle cx="32" cy="15.5" r="1.6" fill="#F6E2A8" />
    </svg>
  );
}

/** Subtle animated dust motes (CSS only) for hero areas. */
export function DustMotes({ count = 18, className }: { count?: number; className?: string }) {
  return (
    <div className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)} aria-hidden>
      {Array.from({ length: count }, (_, i) => {
        const left = (i * 53) % 100;
        const delay = (i * 1.7) % 12;
        const dur = 14 + ((i * 7) % 10);
        const size = 1 + ((i * 3) % 3);
        return (
          <span
            key={i}
            className="absolute rounded-full bg-gold-bright"
            style={{
              left: `${left}%`,
              bottom: '-10px',
              width: size,
              height: size,
              opacity: 0,
              filter: 'blur(.4px)',
              boxShadow: '0 0 6px rgba(232,199,122,.8)',
              animation: `hk-mote ${dur}s linear ${delay}s infinite`,
            }}
          />
        );
      })}
      <style>{`@keyframes hk-mote{0%{transform:translate(0,0);opacity:0}10%{opacity:.55}50%{transform:translate(18px,-50vh)}90%{opacity:.35}100%{transform:translate(-12px,-100vh);opacity:0}}`}</style>
    </div>
  );
}
export { FablekeepMark } from './FablekeepMark';
